import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import type { Dispatch } from 'redux';
import flow from 'lodash/flow';
import debounce from 'lodash/debounce';
import { MediaClientContext, MediaClientProvider } from '@atlaskit/media-client-react';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useCanDeleteAttachment } from '@atlassian/jira-issue-attachments-base/src/controllers/index.tsx';
import { useIssueAttachments } from '@atlassian/jira-issue-attachments-base/src/services/attachments-service/main.tsx';
import AttachmentsError from '@atlassian/jira-issue-attachments-table/src/ui/error/index.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import withViewMediaClientConfig from '@atlassian/jira-issue-media-provider/src/controllers/view-media-client-config/index.tsx';
import type { Attachment } from '@atlassian/jira-issue-shared-types/src/common/types/attachments.tsx';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import {
	cardCoverUpdateRequest,
	type CardCoverUpdateRequestAction,
} from '@atlassian/jira-issue-view-store/src/actions/card-cover-actions.tsx';
import {
	scrollToAttachmentComment,
	type ScrollToAttachmentCommentAction,
	scrollToAttachmentWorklog,
	type ScrollToAttachmentWorklogAction,
} from '@atlassian/jira-issue-view-store/src/actions/issue-scroll-actions.tsx';
import {
	fetchUploadContextRequest,
	type FetchUploadContextRequestAction,
} from '@atlassian/jira-issue-view-store/src/common/media/upload-context/upload-context-actions.tsx';
import {
	fetchViewContextRequest,
	type FetchViewContextRequest,
} from '@atlassian/jira-issue-view-store/src/common/media/view-context/view-context-actions.tsx';
import { isMobileSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/context-selector.tsx';
import { isPreviewSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/issue-selector.tsx';
import { mediaContextSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/media-context-selector.tsx';
import { ModalContextProvider } from '@atlassian/jira-modal-context-provider/src/ModalContextProvider.tsx';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useCanDownloadAttachments } from '@atlassian/jira-project-permissions-service/src/main.tsx';
import {
	useAttachmentStoreIsFetching,
	useAttachmentStoreActions,
} from '@atlassian/jira-issue-attachments-base/src/services/attachments-service/context.tsx';

import type { DispatchProps, Props } from './types.tsx';
import { getActionsForAttachment } from './utils.tsx';
import GridView from './components/GridView.tsx';
import GridItems from './components/GridItems.tsx';
import { useGridContext } from './context/index.tsx';
import { DEBOUNCE_DURATION } from './constants.tsx';

type Action =
	| CardCoverUpdateRequestAction
	| ScrollToAttachmentWorklogAction
	| ScrollToAttachmentCommentAction
	| FetchViewContextRequest
	| FetchUploadContextRequestAction;

const AttachmentGridViewInternal = (props: Props) => {
	const intl = useIntl();
	const { updateTotalItems, gridSize, startIndex } = useGridContext();
	const isFirstRender = useRef(true);

	const {
		isMobile,
		isPreview,
		mediaContext,
		onScrollToComments,
		onScrollToWorklogs,
		viewMediaClientConfig,
	} = props;
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const shouldEnableDownloadButton = useCanDownloadAttachments(projectKey);
	const [isFetching] = useAttachmentStoreIsFetching(issueKey);
	const [, { setMaxResults }] = useAttachmentStoreActions();

	const [
		{
			value: { attachments = [], totalCount },
			error,
		},
		{ refreshAttachments },
	] = useIssueAttachments(issueKey, {
		maxResults: gridSize,
	});

	const canDeleteAttachmentMapModal = useCanDeleteAttachment();

	const items = useMemo(
		() =>
			attachments.map((attachment: Attachment) => {
				const identifier = {
					mediaItemType: 'file',
					id: attachment.mediaApiFileId,
					attachmentId: attachment.id,
					...(mediaContext.viewContext && mediaContext.viewContext.collection !== null
						? { collectionName: mediaContext.viewContext.collection }
						: {}),
				} as const;

				const actions = getActionsForAttachment(
					attachment.id,
					attachment.parentName,
					attachment.parentId,
					canDeleteAttachmentMapModal[attachment.id],
					intl,
					issueKey,
					onScrollToComments,
					onScrollToWorklogs,
				);

				return {
					filename: attachment.filename,
					identifier,
					createdDate: attachment.createdDate ? attachment.createdDate * 1000 : 0,
					mimetype: attachment.mimetype,
					selectable: false,
					actions,
					isLazy: !isMobile,
					isPreview,
					hasRestrictedParent: attachment.hasRestrictedParent,
				};
			}),
		[
			attachments,
			intl,
			isMobile,
			issueKey,
			mediaContext.viewContext,
			onScrollToComments,
			onScrollToWorklogs,
			canDeleteAttachmentMapModal,
			isPreview,
		],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedRefreshAttachments = useCallback(debounce(refreshAttachments, DEBOUNCE_DURATION), [
		refreshAttachments,
	]);

	const onClick = useCallback(() => {
		refreshAttachments(issueKey, {
			startAt: startIndex,
			maxResults: gridSize,
		});
	}, [issueKey, refreshAttachments, startIndex, gridSize]);

	/**
	 * Refresh attachments only on first render.
	 *
	 * There is a issue with the attachments service, that it doesn't refetch
	 * attachments using `useIssueAttachments` hook when totalCount is available.
	 * This is a workaround to refresh attachments when the component is mounted
	 * and totalCount is available.
	 */
	useEffect(() => {
		if (isFirstRender.current && totalCount !== undefined && !isFetching) {
			refreshAttachments(issueKey, {
				startAt: startIndex,
				maxResults: gridSize,
			});
			isFirstRender.current = false;
		}
	}, [issueKey, refreshAttachments, startIndex, gridSize, totalCount, isFetching]);

	// Refresh attachments when there is a changes ingrid size of page number
	useEffect(() => {
		debouncedRefreshAttachments(issueKey, {
			startAt: startIndex,
			maxResults: gridSize,
		});
	}, [issueKey, debouncedRefreshAttachments, startIndex, gridSize]);

	// Update store's maxResults when grid size changes
	useEffect(() => {
		setMaxResults(gridSize);
	}, [setMaxResults, gridSize]);

	useEffect(() => {
		updateTotalItems(totalCount);
	}, [updateTotalItems, totalCount]);

	if (error) {
		return <AttachmentsError shouldHideImage onClick={onClick} />;
	}

	return (
		<ModalContextProvider>
			<GridView>
				<GridItems
					items={items}
					mediaClientConfig={viewMediaClientConfig}
					shouldEnableDownloadButton={shouldEnableDownloadButton}
					totalCount={totalCount}
				/>
			</GridView>
		</ModalContextProvider>
	);
};

export const AttachmentGridView = (props: Props) => {
	const { viewMediaClientConfig } = props;

	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const shouldEnableDownloadButton = useCanDownloadAttachments(projectKey);

	const mediaClientConfig = useMemo(
		() => ({
			...viewMediaClientConfig,
			enforceDataSecurityPolicy:
				fg('block_attachment_download_on_data_export_dsp_rule') && !shouldEnableDownloadButton,
		}),
		[shouldEnableDownloadButton, viewMediaClientConfig],
	);

	return (
		<MediaClientContext.Consumer>
			{(value) =>
				value ? (
					<AttachmentGridViewInternal {...props} viewMediaClientConfig={mediaClientConfig} />
				) : (
					<MediaClientProvider clientConfig={mediaClientConfig}>
						<AttachmentGridViewInternal {...props} viewMediaClientConfig={mediaClientConfig} />
					</MediaClientProvider>
				)
			}
		</MediaClientContext.Consumer>
	);
};

const AttachmentGridViewConnect = flow(
	withViewMediaClientConfig,
	connect(
		(state: State) => ({
			mediaContext: mediaContextSelector(state),
			isMobile: isMobileSelector(state),
			isPreview: isPreviewSelector(state),
		}),
		(dispatch: Dispatch<Action>): DispatchProps => ({
			onCardCoverShow: (currentAttachmentId, newAttachmentId) => {
				dispatch(cardCoverUpdateRequest(currentAttachmentId, newAttachmentId));
			},
			onCardCoverHide: (currentAttachmentId) => {
				dispatch(cardCoverUpdateRequest(currentAttachmentId));
			},
			onScrollToComments: () => {
				dispatch(scrollToAttachmentComment());
			},
			onScrollToWorklogs: () => {
				dispatch(scrollToAttachmentWorklog());
			},
			onViewRefresh: () => {
				dispatch(fetchViewContextRequest());
			},
			onUploadRefresh: () => {
				dispatch(fetchUploadContextRequest());
			},
		}),
	),
)(AttachmentGridView);

export default AttachmentGridViewConnect;
